<template>
  <div class="language-picker" ref="pickerRef">
    <b-button @click="toggleFlyout" variant="primary" id="language-flyout">
      <svg
        class="globe-svg"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 120 120"
        style="enable-background: new 0 0 120 120"
        xml:space="preserve"
      >
        <switch>
          <g>
            <path
              d="M60 120C26.9 120 0 93.1 0 60S26.9 0 60 0s60 26.9 60 60-26.9 60-60 60zM60 5C29.7 5 5 29.7 5 60s24.7 55 55 55 55-24.7 55-55S90.3 5 60 5z"
            />
            <path
              d="M60 120c-19.3 0-34.4-26.4-34.4-60S40.7 0 60 0s34.4 26.4 34.4 60-15.1 60-34.4 60zM60 5C43.8 5 30.5 29.7 30.5 60s13.2 55 29.5 55 29.5-24.7 29.5-55S76.2 5 60 5z"
            />
            <path
              d="M12.2 25.6h95.6v5H12.2zm0 63.9h95.6v5H12.2zm-9.7-32h115v5H2.5z"
            />
            <path d="M57.5 2.5h5v115h-5z" />
          </g>
        </switch>
      </svg>
      <span>
        {{ selectedLanguage ? selectedLanguage.name : this.$t("pages.selfservice.registration.userDetails.selectlanguage") }}
      </span>
      <i class="fa fa-caret-down"></i>
    </b-button>

    <b-popover
      target="language-flyout"
      placement="bottom"
      triggers="manual"
      :show="isFlyoutOpen"
      @hide="isFlyoutOpen = false"
    >
      <div class="language-options">
        <h3 class="heading">{{ $t("Language") }}</h3>
        <b-form-group>
          <b-form-radio-group
            v-model="currentLanguageCode"
            name="languages"
            stacked
            @change="handleLanguageChange"
          >
            <b-form-radio
              v-for="language in availableLanguages"
              :key="language.code"
              :value="language.code"
            >
              {{ language.name }}
            </b-form-radio>
          </b-form-radio-group>
        </b-form-group>
      </div>
    </b-popover>
  </div>
</template>

<script>
import { getClientById } from '../lib/getClientById';
import { getSupportedLocalesByClientId } from '../lib/getSupportedLocalesByClientId';

export default {
    name: 'LanguagePicker',
    data () {
        return {
            isFlyoutOpen: false,
            currentLanguageCode: this.$i18n.locale,
            availableLanguages: getSupportedLocalesByClientId(getClientById())
        };
    },
    computed: {
        selectedLanguage () {
            return this.availableLanguages.find(
                (lang) => lang.code === this.currentLanguageCode
            );
        }
    },
    watch: {
        '$i18n.locale' (newVal) {
            this.currentLanguageCode = newVal.replaceAll('/', '');
        }
    },
    created () {
        const sanitizedI18nLocale = this.$i18n.locale.replace(/[^a-zA-Z-]/g, '');
        // Ensure currentLanguageCode matches the actual locale
        this.currentLanguageCode = sanitizedI18nLocale;
        const urlParams = new URLSearchParams(window.location.search);
        const urlLocale = urlParams.get('locale');
        const defaultLocale = 'en';

        const validLocale = [urlLocale, sanitizedI18nLocale, defaultLocale].find(
            (lang) => lang && this.availableLanguages.some((l) => l.code === lang)
        );

        this.setLanguage(validLocale || defaultLocale);
    },
    methods: {
        toggleFlyout () {
            this.isFlyoutOpen = !this.isFlyoutOpen;
        },
        handleLanguageChange (lang) {
            this.setLanguage(lang);
            const url = new URL(window.location);
            url.searchParams.set('locale', lang);
            window.history.replaceState({}, '', url);

            this.isFlyoutOpen = false;
        },
        setLanguage (lang) {
            if (this.$i18n.locale !== lang) {
                this.$i18n.locale = lang;
            }
            // Force update currentLanguageCode to ensure radio button selection
            this.currentLanguageCode = lang;
        },
        handleDocumentClick (event) {
            const picker = this.$refs.pickerRef;
            if (picker && !picker.contains(event.target)) {
                setTimeout(() => {
                    this.isFlyoutOpen = false;
                }, 0);
            }
        }
    },
    mounted () {
        document.addEventListener('click', this.handleDocumentClick);
        // Force synchronization after component is mounted
        this.$nextTick(() => {
            // Ensure radio button reflects the current locale
            this.currentLanguageCode = this.$i18n.locale;
            // Force Vue to update the radio button selection
            this.$forceUpdate();
        });
    },
    beforeDestroy () {
        document.addEventListener('click', this.handleDocumentClick);
    }
};
</script>

<style scoped>
::v-deep .custom-control-label {
  padding-left: 16px;
  font-size: 14px;
  font-style: normal;
  font-weight: normal;
  color: rgb(37, 37, 37);
}

::v-deep .custom-control-label:before,
::v-deep .custom-control-label:after {
  display: none;
}

::v-deep .popover-body {
  padding: 1px;
}

::v-deep .arrow {
  color: #b3b3bf;
}

::v-deep .custom-control-input {
  display: block;
  opacity: 1;
  position: static;
  z-index: initial;
}

.popover {
  border-color: #b3b3bf;
  border-radius: 5px;
}

.language-picker {
  position: relative;
}

.b-popover {
  width: auto;
  max-width: 243px;
}

.b-form-radio-group {
  margin: 0;
}

#language-flyout {
  background-color: transparent;
  border-color: transparent;
  color: #027fa3;
  display: flex;
  align-items: center;
}

#language-flyout span {
  color: #027fa3;
  font-size: 16px;
  font-family: "Open Sans", sans-serif;
  padding: 0 7px 0 10px;
  font-weight: 400;
  white-space: nowrap;
}

.globe-svg {
  width: 24px;
  height: 24px;
  fill: #027fa3;
}

.heading {
  font-size: 28px;
  line-height: 2.71429rem;
  font-weight: lighter;
  color: #252525;
  margin-bottom: 16px;
  font-family: "Open Sans Complete", Calibri, Tahoma, sans-serif !important;
  line-height: 1;
}

.custom-control {
  padding: 0;
  margin-bottom: 8px;
  display: flex;
}

.language-options {
  width: 230px;
  z-index: 1;
  background: #fff;
  padding: 40px 40px 30px 30px;
}
</style>
